import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPushPublicKey,
  setPushPermission,
  setPushSubscribed,
} from "../redux/slices/notificationsSlice";
import { subscribePush } from "../API";
import { markPushNudgeEnabled } from "../utils/pushNudge";

// PUSH-NUDGE-001 (post-login soft prompt). Extracted from PushSettings.js so the
// bell-dropdown control and the new post-login banner (PushNudgeBanner.js) share ONE
// implementation of "ask for permission and subscribe" rather than drifting apart. The
// public surface (isPushSupported, urlBase64ToUint8Array) and every behavioural detail below
// — including the comments explaining *why* — are carried over unchanged from PushSettings.js;
// see that file's git history for the original review fixes (B1, B2, L5, NEW-M1, R11) this
// logic embodies.
export const isPushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window;

// Standard Web Push VAPID key conversion (MDN's push-notifications sample):
// `PushManager.subscribe` requires a `Uint8Array`, and the backend serves the key
// base64url-encoded.
export const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

// Shared by PushSettings (the bell-dropdown fallback) and PushNudgeBanner (the post-login
// nudge). Both callers get the same `supported`/`pushConfigured`/`pushPermission` values
// (the latter two come straight from the redux slice, so there's one source of truth
// regardless of how many components mount this hook at once) and the same one-shot `enable`
// flow — the ONLY call site for `Notification.requestPermission`, reached exclusively via an
// explicit click handler a caller wires to a button (AC-F6.2, R11: never on load).
//
// Code review F1 — `subscribed` used to be per-hook-instance `useState`, so the bell
// dropdown's PushSettings and the post-login PushNudgeBanner (each mounting this hook
// independently) tracked their own copy: enabling from one left the other still reporting
// "not subscribed" — the banner kept nagging after the bell dropdown had already enabled
// push, and clicking Enable there too double-POSTed a subscribe the server already had. It
// now lives on `state.notifications.pushSubscribed` (see the slice), so every mounted
// instance reads and writes the same value.
const usePushEnable = () => {
  const dispatch = useDispatch();
  const pushPermission = useSelector((state) => state.notifications.pushPermission);
  const pushConfigured = useSelector((state) => state.notifications.pushConfigured);
  const pushPublicKey = useSelector((state) => state.notifications.pushPublicKey);
  const subscribed = useSelector((state) => state.notifications.pushSubscribed);

  // Read once, synchronously, at mount — this never changes over a page's lifetime, and
  // computing it in render (rather than an effect) means the very first render already
  // reflects the real environment (AC-F6.8: an unsupported browser must never flash a push
  // affordance before hiding it).
  const [supported] = useState(isPushSupported);
  const [subscribing, setSubscribing] = useState(false);
  const [error, setError] = useState(null);

  // Code review F10 — guards the one-shot `Notification.requestPermission()` call against a
  // double-click: `subscribing` state alone isn't enough because React batches the
  // `setSubscribing(true)` call, so a second click that lands before the first render
  // commits can still read the stale `subscribing === false`. A ref is synchronous and set
  // before anything awaits, so the second call sees it immediately.
  const pendingRef = useRef(false);

  // Fetch the public key + `enabled` flag once per session (F2 — the slice's `condition`
  // skips this once `pushConfigured` has already resolved), never gated behind a click —
  // only `Notification.requestPermission()` below is (AC-F6.2, R11 is about the permission
  // *prompt*, not this fetch).
  useEffect(() => {
    if (!supported) {
      return;
    }
    dispatch(fetchPushPublicKey());
  }, [dispatch, supported]);

  // Sync the live `Notification.permission` into the slice, and — for an already-granted
  // permission — look up any existing subscription. `getSubscription()` never prompts, so
  // this is safe to call unconditionally and lets a returning user see "on" state without
  // re-clicking Enable.
  useEffect(() => {
    if (!supported) {
      return;
    }
    const permission = Notification.permission;
    dispatch(setPushPermission(permission));
    if (permission === "granted") {
      navigator.serviceWorker.ready
        .then((registration) => registration.pushManager.getSubscription())
        .then((subscription) => dispatch(setPushSubscribed(!!subscription)))
        .catch(() => {
          // Best-effort lookup only — falling through leaves `subscribed` unset, which
          // re-offers the Enable affordance rather than claiming a state we couldn't confirm.
        });
    }
  }, [dispatch, supported]);

  const enable = useCallback(async () => {
    if (pendingRef.current) {
      return;
    }
    pendingRef.current = true;
    setError(null);
    try {
      // The Enable affordance is already gated on `pushConfigured` (and therefore, in
      // practice, on a real key), but this is the last line of defense before the one-shot
      // permission prompt: without it, a missing `pushPublicKey` for any reason burns that
      // prompt and then crashes in `urlBase64ToUint8Array(null)`.
      if (!pushPublicKey) {
        setError("Push isn't available right now. Please try again later.");
        return;
      }
      // The ONLY call site for Notification.requestPermission in this app, reached
      // exclusively via this explicit click handler (AC-F6.2, R11).
      const permission = await Notification.requestPermission();
      dispatch(setPushPermission(permission));
      if (permission !== "granted") {
        return;
      }
      try {
        setSubscribing(true);
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(pushPublicKey),
        });
        const { endpoint, keys } = subscription.toJSON();
        const res = await subscribePush({
          endpoint,
          keys,
          user_agent: navigator.userAgent,
        });
        // `baseApi.js`'s default branch RESOLVES (doesn't reject) on a 5xx or network
        // error, so a failed POST here still returns a value rather than throwing. Checking
        // the status explicitly and throwing routes that failure into the existing `catch`
        // below instead of silently claiming success with no matching subscription row on
        // the server.
        if (res?.status !== 201 && res?.status !== 200) {
          // Roll the browser-side subscription back too. Otherwise the next mount's
          // `getSubscription()` finds this orphan and reports "on" for a subscription the
          // server never recorded, with no way out of that state.
          await subscription.unsubscribe?.().catch?.(() => {});
          throw new Error("subscribe failed");
        }
        // Hand the key to the service worker so a later `pushsubscriptionchange` can
        // re-subscribe without asking the (possibly-closed) app for it first — see the
        // message-protocol comment in public/service-worker.js.
        navigator.serviceWorker.controller?.postMessage({
          type: "store-public-key",
          publicKey: pushPublicKey,
        });
        dispatch(setPushSubscribed(true));
        // Code review F1 — marking the nudge "enabled forever" here (rather than only in
        // PushNudgeBanner's own post-click effect) means the banner hides permanently no
        // matter which UI (bell dropdown PushSettings, or the banner itself) performed the
        // enable.
        markPushNudgeEnabled();
      } catch (err) {
        setError("Couldn't turn on push notifications. Please try again.");
      } finally {
        setSubscribing(false);
      }
    } finally {
      pendingRef.current = false;
    }
  }, [dispatch, pushPublicKey]);

  return {
    supported,
    pushConfigured,
    pushPermission,
    subscribed: !!subscribed,
    subscribing,
    error,
    enable,
  };
};

export default usePushEnable;
